import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Login from '../pages/Login';
import OriginationDashboard from '../pages/OriginationDashboard';
import Origination from '../pages/Origination';
import Clients from '../pages/Clients';
import ClientDetail from '../pages/ClientDetail';
import { useFavicon } from '../hooks/useFavicon';
import Layout from '../organisms/Layout';
import RiskAnalysisDashboard from '../pages/RiskAnalysisDashboard';
import RiskAnalysis from '../pages/RiskAnalysis';
import CommitteeDashboard from '../pages/CommitteeDashboard';
import Committee from '../pages/Committee';
import ClientApplication from '../pages/ClientApplication';
import { ROLES_CAP, COMITE_LEVEL_ROLES } from '../utils/Constants';
import { getRolesFromToken } from '../utils/Utils';
import { hasCommitteeOnlyAccess } from '../utils/uruguayRoles';
import InstrumentationDashboard from '../pages/InstrumentationDashboard';
import InstrumentationSteps from '../pages/InstrumentationSteps';
import { routes } from './routePaths';
import Dashboard from '../pages/Dashboard';
import Instrumentation from '../pages/Instrumentation';
import Users from '../pages/Users';
import NewUser from '../organisms/User/NewUser';
import Confirmation from '../pages/Confirmation';
import ForgotPassword from '../pages/ForgotPassword';
import ResetPassword from '../pages/ResetPassword';
import FinishedFoldersDashboard from '../pages/FinishedFoldersDashboard';
import GuaranteesManagement from '../pages/GuaranteesManagement';
import GuaranteesDetail from '../pages/GuaranteesDetail';
import GroupedProductLineDetail from '../pages/GroupedProductLineDetail';
import ProductLineDetail from '../pages/ProductLineDetail';
import MonetizationDetail from '../pages/MonetizationDetail';
import MonetizationNew from '../pages/MonetizationNew';
import SyndicatedMonetizationNew from '../pages/SyndicatedMonetizationNew';
import MonetizationNewPrefilled from '../pages/MonetizationNewPrefilled';
import MonetizationEdit from '../pages/MonetizationEdit';
import Monetization from '../pages/Monetization';
import ProcessingHistory from '../pages/ProcessingHistory';
import Overdue from '../pages/Overdue';
import Annexes from '../pages/Annexes';
import CasfogSubmissionHistory from '../pages/CasfogSubmissionHistory';
import Settings from '../pages/Settings';
import RateTypes from '../pages/RateTypes';
import Sgr from '../pages/Sgr';
import FundsAndProtectors from '../pages/FundsAndProtectors';
import ProtectorDetail from '../pages/ProtectorDetail';
import ProtectorEdit from '../pages/ProtectorEdit';
import ProtectorNew from '../pages/ProtectorNew';
import ProtectorContributionNew from '../pages/ProtectorContributionNew';
import Partners from '../pages/Partners';
import SharesConfiguration from '../pages/SharesConfiguration';
import Funds from '../pages/Funds';
import AssetsToIntegrate from '../pages/AssetsToIntegrate';
import Quotations from '../pages/Quotations';
import QuotationsDetail from '../pages/QuotationsDetail';
import CommissionsConfiguration from '../pages/CommissionsConfiguration';
import RegulacionSepyme from '../pages/RegulacionSepyme';
import ReferrersConfiguration from '../pages/ReferrersConfiguration';
import Reportes from '../pages/Reportes';
import LoanInstallmentSimulatorPage from '../pages/LoanInstallmentSimulatorPage';
import {
  showCommissionsConfigurationSection,
  type SettingsSectionsConfig,
} from '../utils/settingsTenantSections';

export function shouldApplyFeatureFlagRouteMerges(roles: string[]): boolean {
  return !hasCommitteeOnlyAccess(roles);
}

const origination = `${routes.clients}`;
const originationDetail = `${routes.clients}/:cuit/applications/:id`;

const risksAnalysisDashboard = `${routes.risksAnalysis}`;
const risksAnalysisDetail = `${routes.risksAnalysis}/:cuit/applications/:id`;

const committeeDashboard = `${routes.committee}`;
const committeeDetail = `${routes.committee}/:cuit/applications/:id`;


const clientApplicationDetail = `${routes.clients}/:cuit/application/:id`;

const instrumentationDashboard = `${routes.instrumentation}`;
const instrumentationDetail = `${routes.instrumentation}/:cuit/applications/:id`;
const instrumentationSteps = `${routes.instrumentation}/:cuit/applications/:id/steps`;

const dashboard = `${routes.dashboardComercial}`;
const users = `${routes.users}`;
const newUser = `${routes.users}/nuevoUsuario`;
const finishedFoldersDashboard = `${routes.finishedFolders}`;
const guarantees = `${routes.guarantees}`;
const guaranteeDetail = `${routes.guaranteeDetail}`;
const groupedProductLineDetail = `${routes.groupedProductLineDetail}`;
const productLineDetail = `${routes.productLineDetail}`;
const loanDetail = `${routes.loanDetail}`;
const checkDetail = `${routes.checkDetail}`;
const promissoryNoteDetail = `${routes.promissoryNoteDetail}`;
const commercialEndorsementDetail = `${routes.commercialEndorsementDetail}`;
const monetization = `${routes.monetization}`;
const overdue = `${routes.overdue}`;
const fundsAndProtectors = `${routes.fundsAndProtectors}`;
const protectorDetail = `${routes.protectorDetail}`;

const config = JSON.parse(localStorage.getItem('config') ?? '{}');
const isClientEnabled = config?.isClientEnabled ?? false;
const isGuaranteesEnabled = config?.isGuaranteesEnabled ?? false;
const isQuotationsEnabled = config?.isQuotationsEnabled ?? false;
const isInstrumentationEnabled = config?.isInstrumentationEnabled ?? true;

const withLayout = (Component: React.ComponentType, elementKey?: string) => (
  <Layout>
    <Component key={elementKey} />
  </Layout>
);


const baseOriginationRoutes = [
  { path: routes.dashboard, element: withLayout(OriginationDashboard) },
  { path: origination, element: withLayout(Origination) },
  { path: originationDetail, element: withLayout(Origination) },
];

const riskRoutes = [
  { path: risksAnalysisDashboard, element: withLayout(RiskAnalysisDashboard) },
  { path: risksAnalysisDetail, element: withLayout(RiskAnalysis) },
];

const committeeRoutes = [
  { path: committeeDashboard, element: withLayout(CommitteeDashboard) },
  { path: committeeDetail, element: withLayout(Committee) },
];

const instrumentationRoutes = [
  {
    path: instrumentationDashboard,
    element: withLayout(InstrumentationDashboard),
  },
  { path: instrumentationDetail, element: withLayout(Instrumentation) },
  { path: instrumentationSteps, element: withLayout(InstrumentationSteps) },
];

const finishedFoldersRoutes = [
  { path: finishedFoldersDashboard, element: withLayout(FinishedFoldersDashboard) },
];

const dashboardRoutes = [
  {
    path: dashboard,
    element: withLayout(Dashboard),
  },
];

const usersRoutes = [
  {
    path: users,
    element: withLayout(Users),
  },
  {
    path: newUser,
    element: withLayout(NewUser),
  },
];

const clientRoutes = [
  { path: routes.clientsList, element: withLayout(Clients) },
  { path: routes.clientDetail, element: withLayout(ClientDetail) },
  { path: clientApplicationDetail, element: withLayout(ClientApplication) },
];

const settingsSectionsConfig: SettingsSectionsConfig = {
  country: config?.country,
  isGuaranteesEnabled: config?.isGuaranteesEnabled ?? false,
  isQuotationsEnabled: config?.isQuotationsEnabled ?? false,
};

export function buildSettingsRoutes(
  sectionsConfig: SettingsSectionsConfig,
): { path: string; element: JSX.Element }[] {
  const routeList: { path: string; element: JSX.Element }[] = [
    {
      path: routes.settings,
      element: withLayout(Settings),
    },
    {
      path: routes.quotations,
      element: withLayout(Quotations),
    },
    {
      path: routes.quotationsDetail,
      element: withLayout(QuotationsDetail),
    },
  ];

  if (showCommissionsConfigurationSection(sectionsConfig)) {
    routeList.push({
      path: routes.commissions,
      element: withLayout(CommissionsConfiguration),
    });
  }

  routeList.push({
    path: routes.referrers,
    element: withLayout(ReferrersConfiguration),
  });

  return routeList;
}

const settingsRoutes = buildSettingsRoutes(settingsSectionsConfig);

const guaranteesRoutes = [
  {
    path: guarantees,
    element: withLayout(GuaranteesManagement),
  },
  {
    path: routes.annexes,
    element: withLayout(Annexes),
  },
  {
    path: routes.reportes,
    element: withLayout(Reportes),
  },
  {
    path: routes.casfogHistory,
    element: withLayout(CasfogSubmissionHistory),
  },
  {
    path: guaranteeDetail,
    element: withLayout(GuaranteesDetail),
  },
  {
    path: groupedProductLineDetail,
    element: withLayout(GroupedProductLineDetail),
  },
  {
    path: productLineDetail,
    element: withLayout(ProductLineDetail),
  },
  {
    path: loanDetail,
    element: withLayout(MonetizationDetail),
  },
  {
    path: checkDetail,
    element: withLayout(MonetizationDetail),
  },
  {
    path: promissoryNoteDetail,
    element: withLayout(MonetizationDetail),
  },
  {
    path: commercialEndorsementDetail,
    element: withLayout(MonetizationDetail),
  },
  {
    path: routes.processingHistory,
    element: withLayout(ProcessingHistory),
  },
  {
    path: monetization,
    element: withLayout(Monetization),
  },
  {
    path: routes.loanInstallmentSimulator,
    element: withLayout(LoanInstallmentSimulatorPage),
  },
  {
    path: routes.newMonetization,
    element: withLayout(MonetizationNew),
  },
  {
    path: routes.newSyndicatedMonetization,
    element: withLayout(SyndicatedMonetizationNew),
  },
  {
    path: routes.newRequestLoad,
    element: withLayout(MonetizationNewPrefilled),
  },
  {
    path: routes.editMonetization,
    element: withLayout(MonetizationEdit),
  },
  {
    path: overdue,
    element: withLayout(Overdue),
  },
  {
    path: fundsAndProtectors,
    element: withLayout(FundsAndProtectors),
  },
  {
    path: protectorDetail,
    element: withLayout(ProtectorDetail),
  },
  {
    path: routes.protectorEdit,
    element: withLayout(ProtectorEdit),
  },
  {
    path: routes.protectorNew,
    element: withLayout(ProtectorNew),
  },
  {
    path: routes.protectorContributionNew,
    element: withLayout(ProtectorContributionNew, 'contribution-new'),
  },
  {
    path: routes.protectorContributionEdit,
    element: withLayout(ProtectorContributionNew, 'contribution-edit'),
  },
    {
    path: routes.rateTypes,
    element: withLayout(RateTypes),
  },
  {
    path: routes.sgr,
    element: withLayout(Sgr),
  },
  {
    path: routes.partners,
    element: withLayout(Partners),
  },
  {
    path: routes.shares,
    element: withLayout(SharesConfiguration),
  },
  {
    path: routes.funds,
    element: withLayout(Funds),
  },
  {
    path: routes.assetsToIntegrate,
    element: withLayout(AssetsToIntegrate),
  },
  {
    path: routes.regulacionSepyme,
    element: withLayout(RegulacionSepyme),
  },
];

const routesForProducer = [...baseOriginationRoutes];
let routesForNonProducer = [
  ...baseOriginationRoutes,
  ...riskRoutes,
  ...committeeRoutes,
  ...dashboardRoutes,
  ...usersRoutes,
  ...finishedFoldersRoutes,
];

if (isInstrumentationEnabled) {
  routesForNonProducer = [...routesForNonProducer, ...instrumentationRoutes];
}

if (isClientEnabled) {
  routesForNonProducer = [...routesForNonProducer, ...clientRoutes];
}

let routesForRiskAnalyst = [
  ...baseOriginationRoutes,
  ...riskRoutes,
  ...committeeRoutes,
  ...dashboardRoutes,
  ...finishedFoldersRoutes,
];

if (isInstrumentationEnabled) {
  routesForRiskAnalyst = [...routesForRiskAnalyst, ...instrumentationRoutes];
}

if (isClientEnabled) {
  routesForRiskAnalyst = [...routesForRiskAnalyst, ...clientRoutes];
}

const buildRoutesForAdmin = () => {
  let adminRoutes = [...routesForNonProducer, ...settingsRoutes];
  if (isGuaranteesEnabled) {
    adminRoutes = [...adminRoutes, ...guaranteesRoutes];
  }
  return adminRoutes;
};



const AppRoutes = () => {
  const roles: string[] = getRolesFromToken();
  const isCommitteeOnly = hasCommitteeOnlyAccess(roles);

  useFavicon();

  let allowedRoutes: { path: string; element: JSX.Element }[] = [];

  if (roles.includes(ROLES_CAP.admin)) {
    allowedRoutes = [...allowedRoutes, ...buildRoutesForAdmin()];
  }
  if (roles.includes(ROLES_CAP.producer)) {
    allowedRoutes = [...allowedRoutes, ...routesForProducer];
  }
  if (roles.includes(ROLES_CAP.commercial)) {
    allowedRoutes = [...allowedRoutes, ...routesForNonProducer];
  }
  if (roles.includes(ROLES_CAP.riskAnalyst)) {
    allowedRoutes = [...allowedRoutes, ...routesForRiskAnalyst];
  }
  if (COMITE_LEVEL_ROLES.some(role => roles.includes(role))) {
    allowedRoutes = [...allowedRoutes, ...routesForNonProducer];
  }
  if (roles.includes(ROLES_CAP.instrumentator)) {
    allowedRoutes = [...allowedRoutes, ...routesForNonProducer];
  }
  if (isCommitteeOnly) {
    allowedRoutes = [...allowedRoutes, ...committeeRoutes];
  }
  if (shouldApplyFeatureFlagRouteMerges(roles)) {
    if (isClientEnabled) {
      allowedRoutes = [...allowedRoutes, ...clientRoutes];
    }
    if (isGuaranteesEnabled) {
      allowedRoutes = [...allowedRoutes,  ...guaranteesRoutes];
    }
    if (isQuotationsEnabled || isGuaranteesEnabled) {
      allowedRoutes = [...allowedRoutes, ...settingsRoutes];
    }
  }


  const uniqueRoutes = Array.from(
    new Map(allowedRoutes.map((r) => [r.path, r])).values()
  );

  return (
    <Router>
      <Routes>
        {uniqueRoutes.map((route, index) => (
          <Route key={index} path={route.path} element={route.element} />
        ))}
        <Route path='/confirmacion/:id' element={<Confirmation />} />
        <Route path={routes.forgotPassword} element={<ForgotPassword />} />
        <Route path={routes.resetPassword} element={<ResetPassword />} />
        <Route path='*' element={<Login />} />
      </Routes>
    </Router>
  );
};

export default AppRoutes;
